import React, { useEffect, useState } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import { useCookies } from "react-cookie";
import { ReactComponent as ViewProfile } from "../../images/events/view-profile.svg";
import defaultAvatar from "../../images/default-avatar.svg";
import { apiUrl } from "../../config";
import LocationSearchableDropdown from "./LocationSearchableDropdown";
import LogAndViewOccurrence from "./LogAndViewOccurrence";

const formatDateToTime = (dateStr) => {
  if (!dateStr) return "";
  return new Date(dateStr).toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit"
  });
};

const formatDateToDateTime = (dateStr) => {
  if (!dateStr) return "";

  const rawValue = String(dateStr).trim();
  const isoLikeMatch = rawValue.match(/^\d{4}-\d{2}-\d{2}[T ](\d{2}:\d{2})/);
  if (isoLikeMatch) return isoLikeMatch[1];

  const parsed = new Date(rawValue);
  if (Number.isNaN(parsed.getTime())) return rawValue;

  const hours = String(parsed.getHours()).padStart(2, "0");
  const minutes = String(parsed.getMinutes()).padStart(2, "0");
  return `${hours}:${minutes}`;
};

const buildShiftDetails = (props) => ({
  staffID: props.staffId,
  rotaShiftId: props.rotaShiftId,
  eventLocation: props.location,
  currentLocation: props.location,
  RoleName: props.role,
  RoleId: props.roleid,
  MobileNo: props.MobileNo,
  staffPhoto: props.staffPhoto,
  bookon: props.bookon,
  bookoff: props.bookoff
});

export default function ViewProfileControl(props) {
  const [cookies] = useCookies(["jwt"]);
  const jwt = cookies["jwt"];
  const [show, setShow] = useState(false);
  const [rotaShiftDetails, setRotaShiftDetails] = useState(
    buildShiftDetails(props)
  );
  const [staffPhotoUrl, setStaffPhotoUrl] = useState(null);
  const [assetItems, setAssetItems] = useState([]);
  const [assetRows, setAssetRows] = useState([]);
  const [assetDraft, setAssetDraft] = useState({
    itemId: 0,
    qty: "1",
    reference: ""
  });
  const [assetLoading, setAssetLoading] = useState(false);
  const [assetError, setAssetError] = useState("");
  const [nextId, setNextId] = useState(1);

  useEffect(() => {
    if (!show || !props.staffId) return;

    const fetchPhoto = async () => {
      try {
        const response = await fetch(
          `${apiUrl}/Events/GetStaffPhoto/${props.staffId}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${jwt}`
            }
          }
        );

        if (!response.ok) {
          throw new Error(`Photo request failed: ${response.status}`);
        }

        const result = await response.json();
        setStaffPhotoUrl(result.data || null);
      } catch (error) {
        setStaffPhotoUrl(null);
      }
    };

    fetchPhoto();
  }, [show, props.staffId, jwt]);

    useEffect(() => {
    if (!show) return;

    const fetchAssetItems = async () => {
      setAssetLoading(true);
      setAssetError("");
        try {

         const response = await fetch(
             `${apiUrl}/Events/Items`,
              {
                  method: "GET",
                  headers: {
                        Authorization: `Bearer ${jwt}`
                  }
              }
            );

            const result = await response.json();
            setAssetItems(result.data || null);
      } catch (error) {
        setAssetError("Unable to load items.");
        setAssetItems([]);
      } finally {
        setAssetLoading(false);
      }
      };
      fetchAssetItems();
    }, [show, jwt]);

    useEffect(() => {
        if (assetItems !== null) { // Only run when assetItems is loaded
            fetchShiftItems();
        }
    }, [assetItems]);

    const fetchShiftItems = async () => {
        try {
            const response = await fetch(
                `${apiUrl}/Events/ShiftItems/${101}`,
                {
                    method: "GET",
                    headers: {
                        Authorization: `Bearer ${jwt}`
                    }
                }
            );

            if (!response.ok) {
                throw new Error(`Shift items fetch request failed: ${response.status}`);
            }

            const result = await response.json();

            const normalized = (result.data || []).map((item, index) => ({
                rowId: index + 1,
                id: item.id, 
                itemId: item.itemId,
                itemName: item.description,
                quantity: item.quantity,
                reference: item.reference || "",
                returned: false,
                originalQty: item.quantity,
                originalReference: item.reference || "",
                itemName: assetItems.find(
                    (val) => val.id === item.itemId
                )?.description,
                returnQuantity: item.returnQuantity ?? 0
            }));

            setAssetRows(normalized || null);

            const maxRowId = normalized.reduce(
                (max, row) => Math.max(max, row.rowId),
                0
            );

            setNextId(maxRowId + 1);
        } catch (error) {
            setAssetRows(null);
        }
    };

  const handleOpen = () => {
    setRotaShiftDetails(buildShiftDetails(props));
    setAssetRows([]);
    setAssetDraft({
      itemId: 0,
      qty: "1",
      reference: ""
    });
    setShow(true);
  };

  const handleClose = () => setShow(false);

  const handleApply = async () => {
    try {
      const response = await fetch(`${apiUrl}/Events/UpdateShift`, {
        method: "POST",
        headers: {
          Authorization: `bearer ${jwt}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify(rotaShiftDetails)
      });

      const updatedShift = await response.json();
      const selectedRole = props.roleOptions.find(
        (item) => String(item.roleId) === String(rotaShiftDetails.RoleId)
      );
      const nextLocation =
        updatedShift?.data?.eventLocation || rotaShiftDetails.eventLocation;
      const nextRoleId =
        updatedShift?.data?.roleId ??
        selectedRole?.roleId ??
        rotaShiftDetails.RoleId;
      const nextRoleName =
        updatedShift?.data?.roleName ||
        selectedRole?.roleName ||
        rotaShiftDetails.RoleName;

      setRotaShiftDetails((prev) => ({
        ...prev,
        eventLocation: nextLocation,
        currentLocation: nextLocation,
        RoleId: nextRoleId,
        RoleName: nextRoleName
      }));

      props.onShiftUpdated({
        rotaShiftId: props.rotaShiftId,
        eventLocation: nextLocation,
        roleId: nextRoleId,
        roleName: nextRoleName
      });

      handleClose();
    } catch (error) {
      console.error(error);
    }
  };

  const handleModalBookOnOff = async () => {
    await props.onBookOnOff(
      rotaShiftDetails.rotaShiftId,
      rotaShiftDetails.bookon == null ? 1 : 0
    );
    handleClose();
  };

    const handleAddAssetRow = () => {
    if (!assetDraft.itemId) return;

    const qtyNum = Number(assetDraft.qty);
    const safeQty =
      Number.isFinite(qtyNum) && qtyNum > 0 ? Math.floor(qtyNum) : 1;
    const selectedItem = assetItems.find(
      (item) => item.id == Number(assetDraft.itemId)
    );

    const nextRow = {
      rowId: nextId,
      id: null, 
      itemId: Number(assetDraft.itemId),
      itemName: selectedItem?.description || "",
      quantity: safeQty,
      reference: assetDraft.reference.trim(),
      returned: false,
      originalQty: safeQty,
      originalReference: assetDraft.reference.trim(),
      dateTimeCreated: new Date()
    };

    setAssetRows((prev) => [...prev, nextRow]);
    setAssetDraft((prev) => ({
      ...prev,
      itemId: 0,
      qty: "1",
      reference: ""
    }));
    setNextId(prev => prev + 1);
  };

  const handleToggleReturned = (rowId) => {
    setAssetRows((prev) =>
      prev.map((row) =>
        row.rowId === rowId ? { ...row, returned: !row.returned } : row
      )
    );
  };

  const handleResetRow = (rowId) => {
    setAssetRows((prev) =>
      prev.map((row) =>
        row.rowId === rowId
          ? {
              ...row,
              qty: row.originalQty,
              reference: row.originalReference,
              returned: false
            }
          : row
      )
    );
  };

  const handleDeleteRow = (rowId) => {
    setAssetRows((prev) => prev.filter((row) => row.rowId !== rowId));
  };

  const handleReturnAssets = () => {
    const selectedForReturn = assetRows.filter((row) => row.returned);
    console.log("TODO: return selected assets", selectedForReturn);
  };

    const handleSaveAssetAllocation = async () => {
        debugger
        try {
            const payload = {
                rotaShiftId: 101, // make sure this variable exists
                items: assetRows
            };

            const response = await fetch(`${apiUrl}/Events/SaveShiftItems`, {
                method: "POST",
                headers: {
                    Authorization: `bearer ${jwt}`,
                    "Content-Type": "application/json"
                },
                body: JSON.stringify(payload)
            });

            if (response.ok) {
                fetchShiftItems();
            }
        }         
        catch (error) {
            throw new Error(`Shift items save request failed`);
        }
    };

    const handleCopyQty = (rowId) => {
        setAssetRows(prev =>
            prev.map(row =>
                row.rowId === rowId
                    ? { ...row, returnQuantity: row.quantity }
                    : row
            )
        );
    };

    const handleReduceQty = (rowId) => {
        setAssetRows(prev =>
            prev.map(row =>
                row.rowId === rowId && row.returnQuantity > 0
                    ? { ...row, returnQuantity: row.returnQuantity - 1 }
                    : row
            )
        );
    };

  return (
      <>
      {typeof props.renderTrigger === "function" ? (
        props.renderTrigger(handleOpen)
      ) : (
        <button
          type="button"
          title="View Profile"
          className="rounded-button ms-2"
          onClick={handleOpen}
        >
          <ViewProfile />
        </button>
      )}

      <Modal
        show={show}
        onHide={handleClose}
        autoFocus={false}
        restoreFocus={false}
        enforceFocus={false}
        size="xl"
        centered
      >
        <Modal.Header closeButton closeVariant="white">
          <Modal.Title>
            {props.FirstName} {props.LastName}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="view-profile-modal-body" style={{ overflowX: "hidden" }}>
          <div className="view-profile-top-row">
            <div
              className="profile-image-container me-3"
              style={{
                width: "125px",
                height: "125px",
                borderRadius: "50%",
                overflow: "hidden",
                border: "2px solid #E23193",
                flexShrink: 0
              }}
            >
              <img
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
                src={
                  staffPhotoUrl &&
                  staffPhotoUrl.startsWith("data:image/jpeg;base64,")
                    ? staffPhotoUrl
                    : defaultAvatar
                }
                alt="Profile"
              />
            </div>
            <div className="profile-info">
              <Form.Group
                className="mb-2"
                controlId={`profile-location-${props.rotaShiftId}`}
              >
                <label>Location</label>
                <LocationSearchableDropdown
                  id={`profile-location-input-${props.rotaShiftId}`}
                  value={rotaShiftDetails.eventLocation || ""}
                  onChange={(selectedValue) =>
                    setRotaShiftDetails((prev) => ({
                      ...prev,
                      eventLocation: selectedValue || ""
                    }))
                  }
                  options={props.locationOptions}
                  placeholder="Select or type location"
                />
              </Form.Group>

              <Form.Group
                className="mb-2"
                controlId={`profile-role-${props.rotaShiftId}`}
              >
                <label>Role</label>
                <Form.Select
                  aria-label="Role"
                  value={rotaShiftDetails.RoleId || ""}
                  onChange={(e) => {
                    const id = Number(e.target.value);
                    const selected = props.roleOptions.find(
                      (item) => item.roleId === id
                    );
                    setRotaShiftDetails((prev) => ({
                      ...prev,
                      RoleId: id,
                      RoleName: selected?.roleName || ""
                    }));
                  }}
                >
                  <option value="">Select Role</option>
                  {props.roleOptions.map((item) => (
                    <option key={item.roleId} value={item.roleId}>
                      {item.roleName}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              <p className="mb-1">
                Contact:
                <button
                  type="button"
                  onClick={() =>
                    (window.location.href = `tel:${props.MobileNo}`)
                  }
                  className="telink ms-1"
                  style={{
                    background: "none",
                    border: "none",
                    padding: 0,
                    cursor: "pointer",
                    textDecoration: "underline",
                    color: "inherit"
                  }}
                >
                  {props.MobileNo}
                </button>
              </p>
              <p className="mb-1">
                Scheduled Times: {formatDateToDateTime(props.start)} -{" "}
                {formatDateToDateTime(props.finish)}
              </p>
              <p className="mb-0">
                Attended Times:{" "}
                <span className="text-success">
                  {props.bookon ? formatDateToTime(props.bookon) : null}
                </span>{" "}
                - {props.bookoff ? formatDateToTime(props.bookoff) : null}
              </p>
            </div>
          </div>

          <div className="asset-allocation-section">
            <h5 className="mb-3">Asset Allocation</h5>

            <div style={{ marginBottom: "12px" }}>
              <div
                className="asset-allocation-input-row"
                style={{
                  display: "grid",
                  gridTemplateColumns:
                    "minmax(0, 1.4fr) 90px minmax(0, 1.8fr) 130px",
                  gap: "10px 12px",
                  alignItems: "end"
                }}
              >
                  <Form.Group className="asset-input-item mb-0">
                    <Form.Label>Items</Form.Label>
                    <Form.Select
                      value={assetDraft.itemId}
                      onChange={(e) =>
                        setAssetDraft((prev) => ({
                          ...prev,
                            itemId: e.target.value
                        }))
                      }
                      disabled={assetLoading}
                    >
                      <option value="">Select</option>
                      {assetItems.map((item) => (
                          <option key={item.id} value={item.id}>
                              {item.description}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>

                              <Form.Group className="asset-input-qty mb-0">
                    <Form.Label>Qty</Form.Label>
                                  <Form.Control
                                      type="number"
                                      min="1"
                                      value={assetDraft.qty}
                                      onChange={(e) =>
                                          setAssetDraft((prev) => ({
                                              ...prev,
                                              qty: e.target.value
                                          }))
                                      }
                                      style={{ height: "38px" }}
                    />
                  </Form.Group>

                  <Form.Group className="asset-input-reference mb-0">
                    <Form.Label>Reference</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Reference Number"
                      value={assetDraft.reference}
                      onChange={(e) =>
                        setAssetDraft((prev) => ({
                          ...prev,
                          reference: e.target.value
                        }))
                      }
                    />
                  </Form.Group>

                  <div className="asset-input-add">
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={handleAddAssetRow}
                      className="w-100"
                    >
                      Add Item
                    </Button>
                  </div>
              </div>
            </div>

            {assetError ? (
              <div className="text-danger mb-2">{assetError}</div>
            ) : null}

            <div className="table-responsive mb-3">
              <table className="table table-sm align-middle mb-0 asset-allocation-table">
                <thead>
                  <tr>
                    <th>Item</th>
                    <th className="asset-col-qty">Qty</th>
                                      <th>Reference</th>
                                      {rotaShiftDetails.bookon != null && <th>Return Qty</th>}
                    <th className="asset-col-actions">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {assetRows.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="text-muted">
                        No assets added yet.
                      </td>
                    </tr>
                  ) : (
                    assetRows.map((row) => (
                      <tr key={row.rowId}>
                            <td>{row.itemName}</td>                           
                            <td>{row.quantity}</td>
                            <td>{row.reference}</td>

                            {rotaShiftDetails.bookon != null && <td>
                                <div className="d-flex align-items-center gap-2">
                                    <span>{row.returnQuantity}</span>

                                    <button
                                        type="button"
                                        className="btn btn-sm btn-outline-primary"
                                        onClick={() => handleCopyQty(row.rowId)}
                                        title="Copy Qty"
                                    >
                                        Copy
                                    </button>

                                    <button
                                        type="button"
                                        className="btn btn-sm btn-outline-warning"
                                        onClick={() => handleReduceQty(row.rowId)}
                                        title="Reduce Qty"
                                        disabled={row.returnQuantity <= 0}
                                    >
                                        -
                                    </button>
                                </div>
                            </td>}                      
                        <td>
                          <div className="asset-row-actions d-flex gap-2">
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => handleDeleteRow(row.rowId)}
                              title="Delete row"
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className="asset-allocation-footer">
              <div className="asset-footer-actions">
                <Button
                  type="button"
                  variant="outline-secondary"
                  onClick={handleReturnAssets}
                >
                  Return Assets
                </Button>
                <Button
                  type="button"
                  variant="primary"
                  onClick={handleSaveAssetAllocation}
                >
                  Save Asset Allocation
                </Button>
              </div>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <LogAndViewOccurrence
            rsid={props.rotaShiftId}
            fetchOccurrencesByRsid={props.fetchOccurrencesByRsid}
          />
          <Button
            type="button"
            variant={rotaShiftDetails.bookon == null ? "primary" : "secondary"}
            disabled={
              rotaShiftDetails.bookoff && rotaShiftDetails.bookon != null
            }
            onClick={handleModalBookOnOff}
          >
            {rotaShiftDetails.bookoff != null
              ? "Booked off"
              : rotaShiftDetails.bookon == null
              ? "Book on"
              : "Book off"}
          </Button>
          <Button type="button" variant="primary" onClick={handleApply}>
            Apply
          </Button>
          <Button type="button" variant="secondary" onClick={handleClose}>
            Close
          </Button>
        </Modal.Footer>
              </Modal>
    </>
  );
}
